import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["arrow", "autofill", "error", "fieldTooltip", "fieldTooltipDoNotFold", "hasFluidWidth", "ariaLabel", "disabled", "icon", "ids", "onBlur", "onClick", "onFocus", "UNSAFE_containerProps", "styleSheet", "warning"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React from 'react';
import { compose, defaultProps, setDisplayName, shouldUpdate, withProps } from 'recompose';
import omitProps from '@hocs/omit-props';
import { withFocusProps, withHoverProps, withUncontrolledProp } from '@klarna/higher-order-components';
import TouchableIcon from '@bubble-ui/molecule-touchable-icon';
import IconError from '@bubble-ui/icon-error';
import IconWarning from '@bubble-ui/icon-warning';
import IconHelp from '@bubble-ui/icon-help';
import Tooltip from '@bubble-ui/molecule-tooltip';
import wrapOverridableComponent from '@bubble-ui/hoc-wrap-overridable-component';
import * as Layers from '@bubble-ui/particle-layers';
import View from '@bubble-ui/primitive-view';
import withPropDeprecationWarning from '@bubble-ui/hoc-with-prop-deprecation-warning';

var getDefaultStyleSheet = function getDefaultStyleSheet(_ref) {
  var active = _ref.active,
      bottomArrow = _ref.bottomArrow,
      withPadding = _ref.withPadding,
      icon = _ref.icon;
  return {
    rootStyle: {
      cursor: icon ? 'pointer' : 'auto',
      position: 'initial',
      zIndex: Layers.TOOLTIPICON
    },
    iconStyle: {
      alignSelf: 'center',
      paddingRight: withPadding ? 15 : 0,
      position: 'absolute',
      right: 0,
      top: '50%',
      transform: 'translateY(-50%)'
    },
    hoverElementStyle: {
      marginTop: -20,
      opacity: active ? 1 : 0,
      paddingTop: 17.5,
      paddingBottom: 22.5,
      paddingLeft: '33%',
      position: 'absolute',
      right: 0,
      top: '50%',
      visibility: active ? 'visible' : 'hidden',
      zIndex: Layers.TOOLTIPHOVER
    },
    tooltipStyle: _objectSpread(_objectSpread({}, bottomArrow ? {
      bottom: 45
    } : {
      top: 45
    }), {}, {
      marginRight: withPadding ? 10 : -15,
      opacity: active ? 1 : 0,
      position: 'absolute',
      right: withPadding ? -10 : 0,
      visibility: active ? 'visible' : 'hidden',
      zIndex: Layers.TOOLTIP
    })
  };
};

var getIds = function getIds(_ref2) {
  var id = _ref2.id;
  return {
    ids: id ? {
      root: id,
      iconWrapper: "".concat(id, "__icon-wrapper"),
      icon: "".concat(id, "__icon"),
      span: "".concat(id, "__span"),
      tooltipWrapper: "".concat(id, "__tooltip-wrapper"),
      tooltip: "".concat(id, "__tooltip")
    } : {}
  };
};

function FieldTooltip(_ref3) {
  var arrow = _ref3.arrow,
      autofill = _ref3.autofill,
      error = _ref3.error,
      fieldTooltip = _ref3.fieldTooltip,
      fieldTooltipDoNotFold = _ref3.fieldTooltipDoNotFold,
      hasFluidWidth = _ref3.hasFluidWidth,
      ariaLabel = _ref3.ariaLabel,
      disabled = _ref3.disabled,
      icon = _ref3.icon,
      ids = _ref3.ids,
      onBlur = _ref3.onBlur,
      onClick = _ref3.onClick,
      onFocus = _ref3.onFocus,
      UNSAFE_containerProps = _ref3.UNSAFE_containerProps,
      _ref3$styleSheet = _ref3.styleSheet,
      iconStyle = _ref3$styleSheet.iconStyle,
      hoverElementStyle = _ref3$styleSheet.hoverElementStyle,
      tooltipStyle = _ref3$styleSheet.tooltipStyle,
      rootStyle = _ref3$styleSheet.rootStyle,
      warning = _ref3.warning,
      props = _objectWithoutProperties(_ref3, _excluded);

  var IconType = icon ? icon : error ? IconError : warning ? IconWarning : IconHelp;
  return /*#__PURE__*/React.createElement(View, _extends({}, UNSAFE_containerProps, {
    id: ids.root,
    onClick: onClick,
    style: rootStyle
  }, props), /*#__PURE__*/React.createElement(View, {
    id: ids.iconWrapper,
    style: iconStyle
  }, /*#__PURE__*/React.createElement(TouchableIcon, {
    Icon: IconType,
    onFocus: onFocus,
    onBlur: onBlur,
    grey: !error && !warning && !disabled && !autofill,
    ariaLabel: ariaLabel
  })), /*#__PURE__*/React.createElement("span", {
    id: ids.span,
    style: hoverElementStyle
  }), /*#__PURE__*/React.createElement(View, {
    id: ids.tooltipWrapper,
    style: tooltipStyle
  }, /*#__PURE__*/React.createElement(Tooltip, _extends({
    hasFluidWidth: hasFluidWidth,
    arrow: arrow,
    id: ids.tooltip
  }, fieldTooltipDoNotFold === undefined ? {} : {
    isLong: !fieldTooltipDoNotFold
  }), fieldTooltip)));
}

export default compose(setDisplayName('FieldTooltip'), defaultProps({
  active: false
}), withHoverProps({
  active: true
}), withFocusProps({
  active: true
}), withUncontrolledProp({
  prop: 'active',
  handlers: {
    onClick: function onClick(props) {
      return function () {
        return props.active = true;
      };
    }
  }
}), shouldUpdate(function (prevProps, nextProps) {
  if (prevProps.active !== nextProps.active) {
    nextProps.onActive(nextProps.active);
  }

  return prevProps.active !== nextProps.active || prevProps.warning !== nextProps.warning || prevProps.error !== nextProps.error;
}), withProps(function (_ref4) {
  var arrow = _ref4.arrow;
  return {
    bottomArrow: arrow && arrow.indexOf('bottom') !== -1
  };
}), wrapOverridableComponent('Field.FieldTooltip', getDefaultStyleSheet), withProps(getIds), omitProps('big', 'bottomArrow', 'id', 'withPadding', 'active', 'onActive'), withPropDeprecationWarning({
  name: 'fieldTooltipDoNotFold',
  removalDate: 'v5.0.0'
}))(FieldTooltip);
//# sourceMappingURL=index.js.map